import { Injectable } from '@angular/core';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';
import { PatientDisplayModeService } from '@modules/patient/services/patient-display-mode.service';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { PatientAppAdapter } from '@shared/services/patient-app-adapter.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class RxIntuitivePatientDetailsFacade {
	readonly isPatientAppReady$ = this.patientAppAdapter.isPatientAppReady$;
	readonly isNewPatient$ = this.patientQuery.patientGuid$.pipe(map(guid => !guid));
	readonly patientDisplayMode$: Observable<PatientDisplayMode> = this.patientDisplayModeService.displayMode$;

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly patientQuery: PatientQuery,
		private readonly patientAppAdapter: PatientAppAdapter,
		private readonly patientDisplayModeService: PatientDisplayModeService
	) {}

	validatePatientDetailsForSave(): Observable<boolean> {
		if (this.shellQuery.isReadOnly) {
			return of(true);
		}

		return combineLatest([
			this.patientQuery.patient$,
			this.patientQuery.isPatientInConflict$,
			this.patientDisplayMode$,
			this.patientQuery.isPatientValid$
		]).pipe(
			map(([patient, isPatientInConflict, displayMode, isValid]) => {
				const hasPatientWithoutConflicts = !!patient && !isPatientInConflict;

				return displayMode === PatientDisplayMode.PatientCard ? hasPatientWithoutConflicts && isValid : hasPatientWithoutConflicts;
			})
		);
	}

	savePatientDetails(): Observable<boolean> {
		return this.validatePatientDetailsForSave().pipe(
			take(1),
			withLatestFrom(this.patientQuery.hasUnsavedChanges$, this.patientDisplayMode$),
			switchMap(([isPatientAppValid, hasUnsavedChanges, displayMode]) => {
				const saveEnabled = isPatientAppValid && !this.shellQuery.isReadOnly && this.patientQuery.isPatientAppConfigured;
				const saveRequired = displayMode === PatientDisplayMode.PatientCard ? hasUnsavedChanges : !this.patientQuery.patientGuid;

				if (saveEnabled && saveRequired) {
					return this.patientAppAdapter.savePatient();
				}

				return of(isPatientAppValid);
			})
		);
	}
}
